import React from "react";
import { JsonLd, SITE_URL, SOCIAL_PROFILES } from "@/lib/seo";
import schedule from "@/data/schedule.generated.json";

const PLACE = {
  "@type": "Place",
  name: "Autoescuelas Faro - Plaza América",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Centro Comercial 3 Plaza América, Local 12",
    addressLocality: "Vigo",
    addressRegion: "Pontevedra",
    postalCode: "36211",
    addressCountry: "ES",
  },
};

const PROVIDER = {
  "@type": ["LocalBusiness", "EducationalOrganization"],
  "@id": "https://autoescuelasfaro.com/autoescuela-plaza-america#business",
  name: "Autoescuelas Faro",
  url: "https://autoescuelasfaro.com/",
  sameAs: SOCIAL_PROFILES,
  identifier: {
    "@type": "PropertyValue",
    name: "Autorización DGT",
    value: "360024",
  },
  hasCredential: {
    "@type": "EducationalOccupationalCredential",
    credentialCategory: "Autorización administrativa",
    name: "Centro de formación autorizado por la Dirección General de Tráfico nº 360024",
    recognizedBy: {
      "@type": "GovernmentOrganization",
      name: "Dirección General de Tráfico",
      url: "https://www.dgt.es",
    },
  },
};

// tipo: "puntos" | "carnet". Genera Course + hasCourseInstance a partir del
// schedule resuelto en build. Sin fetch en cliente.
export function buildCalendarCourse(tipo) {
  const code = tipo === "carnet" ? "B1" : "A";
  const meta = schedule.meta[code];
  // El JSON se filtra al compilar; aqui se vuelve a filtrar para que una
  // convocatoria que pase entre dos builds no siga en el schema renderizado.
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" });
  const list = (schedule.convocatorias[code] || []).filter((c) => (c.endDay || c.startDay) >= today);
  const instances = list.map((c) => ({
    "@type": "CourseInstance",
    courseMode: c.courseMode,
    courseWorkload: meta.timeRequired,
    startDate: c.startDate,
    endDate: c.endDate,
    location: PLACE,
    offers: {
      "@type": "Offer",
      price: c.price,
      priceCurrency: c.priceCurrency,
      availability: "https://schema.org/InStock",
    },
  }));
  return {
    "@type": "Course",
    "@id": `${SITE_URL}${meta.path}#course`,
    name: meta.name,
    description: meta.description,
    url: `${SITE_URL}${meta.path}`,
    inLanguage: "es-ES",
    provider: PROVIDER,
    educationalCredentialAwarded: meta.credential,
    timeRequired: meta.timeRequired,
    offers: {
      "@type": "Offer",
      price: meta.price,
      priceCurrency: meta.priceCurrency,
      category: "Paid",
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}${meta.path}#${meta.anchor}`,
    },
    ...(instances.length ? { hasCourseInstance: instances } : {}),
  };
}

export const CalendarCourseJsonLd = ({ tipo }) => (
  <JsonLd data={{ "@context": "https://schema.org", ...buildCalendarCourse(tipo) }} />
);

// ItemList referencial: cada ListItem apunta al Course canónico de su landing
// (por @id + url), NO duplica el Course completo. Evita duplicación semántica.
export const CoursesItemListJsonLd = () => {
  const puntosPath = schedule.meta.A.path;
  const carnetPath = schedule.meta.B1.path;
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            url: `${SITE_URL}${puntosPath}`,
            name: schedule.meta.A.name,
          },
          {
            "@type": "ListItem",
            position: 2,
            url: `${SITE_URL}${carnetPath}`,
            name: schedule.meta.B1.name,
          },
        ],
      }}
    />
  );
};

// Course del CICLO ADICIONAL: 4 h, sin schedule fijo (se programa a demanda).
// hasCourseInstance vacío por diseño (individual, no convocatoria de grupo).
// Deja meta y precio a criterio del consumidor (props).
export const CourseSchemaJsonLd = ({ name, description, price, path, timeRequired }) => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "Course",
      name,
      description,
      timeRequired,
      provider: PROVIDER,
      hasCourseInstance: { "@type": "CourseInstance", courseMode: "Blended" },
      offers: {
        "@type": "Offer",
        price,
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}${path}`,
      },
    }}
  />
);
